// AI Service for OpenAI integration
// IMPORTANT: All API calls go through the server proxy for credit tracking
// DO NOT use client-side SDK - it bypasses credit deduction
import type OpenAI from 'openai'; // Types only, no runtime SDK
import { LIMITS, MODELS, API_ERRORS } from '../constants/app';
import logger from '../utils/logger';
import { supabase } from '../lib/supabase';
import { optimizeImage, formatFileSize } from '../utils/imageOptimization';
import type { ChatMessage, ConversationContext } from '../types/api';

interface OpenAIResponse {
  content: string;
  context: ConversationContext;
  type?: 'text' | 'image';
}

interface VisionOptions {
  detail?: 'auto' | 'low' | 'high';
  maxTokens?: number;
  temperature?: number;
}

class OpenAIService {
  private apiBase: string;

  constructor() {
    // Use relative URL in production, localhost for development
    this.apiBase = process.env.REACT_APP_API_BASE || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');
    logger.info('[OpenAIService] Initialized - all requests go through server proxy at', this.apiBase);
  }

  /**
   * Convert any image URL (data:, blob:, http, relative /) to optimized base64 data URL
   * Automatically resizes and compresses large images
   */
  private async toBase64DataUrl(imageUrl: string): Promise<string | null> {
    try {
      // Use image optimization to resize/compress large images
      const result = await optimizeImage(imageUrl, {
        maxDimension: 2048,  // OpenAI can handle larger but 2K is plenty
        maxFileSizeMB: 4,    // Keep under 5MB for safe API transfer
        preserveTransparency: false // JPEG compression for smaller size
      });
      
      if (result.wasOptimized) {
        logger.info(`[OpenAIService] Image optimized: ${formatFileSize(result.originalSize)} → ${formatFileSize(result.optimizedSize)}`);
      }
      
      return result.dataUrl;
    } catch (e) {
      logger.warn('[OpenAIService] Failed to optimize image, attempting raw conversion:', e);
      
      // Fallback to raw conversion if optimization fails
      try {
        if (imageUrl.startsWith('data:')) {
          return imageUrl;
        }
        
        if (imageUrl.startsWith('blob:') || imageUrl.startsWith('http') || imageUrl.startsWith('/')) {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          
          const base64Data: string = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result;
              if (typeof result === 'string') {
                resolve(result);
              } else {
                reject(new Error('FileReader result is not a string'));
              }
            };
            reader.onerror = (err) => reject(err);
            reader.readAsDataURL(blob);
          });
          
          logger.debug('[OpenAIService] Converted image to base64, size:', base64Data.length);
          return base64Data;
        }
        
        logger.warn('[OpenAIService] Unsupported image URL format:', imageUrl.substring(0, 50));
        return null;
      } catch (fetchErr) {
        logger.warn('[OpenAIService] Failed to fetch image for conversion:', imageUrl, fetchErr);
        return null;
      }
    }
  }

  /**
   * Generate response with conversation context
   * All requests go through server proxy for credit tracking
   */
  async generateResponse(
    prompt: string,
    systemPrompt: string | null = null,
    context: ConversationContext | null = null,
    imageUrls?: string[]
  ): Promise<OpenAIResponse> {
    const tStart = performance.now();
    if (!prompt || prompt.trim().length === 0) {
      throw new Error('Prompt cannot be empty');
    }

    const messages: Array<OpenAI.Chat.ChatCompletionMessageParam> = [];

    // Add system prompt if provided
    if (systemPrompt) {
      messages.push({
        role: 'system',
        content: systemPrompt
      });
    }

    // Helper to convert ArrayBuffer to base64
    const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
      let binary = '';
      const bytes = new Uint8Array(buffer);
      const len = bytes.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return btoa(binary);
    };

    // Helper to inline blob URLs into data URLs; pass through http(s) and existing data URLs
    const inlineImageUrl = async (url: string): Promise<{ url: string; detail?: 'auto' | 'low' | 'high' }> => {
      try {
        if (!url) return { url };
        let actualUrl = url;
        if (actualUrl.startsWith('__BLOB_URL__:')) {
          actualUrl = actualUrl.replace('__BLOB_URL__:', '');
        }
        if (actualUrl.startsWith('data:')) {
          return { url: actualUrl, detail: 'auto' };
        }
        if (actualUrl.startsWith('blob:')) {
          const resp = await fetch(actualUrl);
          const contentType = resp.headers.get('content-type') || 'image/png';
          const buf = await resp.arrayBuffer();
          const b64 = arrayBufferToBase64(buf);
          return { url: `data:${contentType};base64,${b64}`, detail: 'auto' };
        }
        // For http(s), let OpenAI fetch directly
        return { url: actualUrl, detail: 'auto' };
      } catch (e) {
        // On failure, fall back to passing original URL
        return { url, detail: 'auto' };
      }
    };

    // Add context if provided (with windowing to prevent memory leak)
    const tContextStart = performance.now();
    if (context && context.messages) {
      const recentMessages = context.messages.slice(-LIMITS.MAX_CONTEXT_MESSAGES);

      for (const msg of recentMessages) {
        if (typeof msg.content === 'string') {
          // Simple text message
          messages.push({
            role: msg.role,
            content: msg.content
          } as OpenAI.Chat.ChatCompletionMessageParam);
        } else if (Array.isArray(msg.content)) {
          // Multimodal message with text and images
          const contentParts: Array<OpenAI.Chat.ChatCompletionContentPart> = [];

          for (const part of msg.content) {
            if (part.type === 'text') {
              contentParts.push({
                type: 'text',
                text: part.text
              });
            } else if (part.type === 'image') {
              const tInlineStart = performance.now();
              const inlined = await inlineImageUrl(part.imageUrl);
              logger.debug('[OpenAIService] Inlined image in', Math.round(performance.now() - tInlineStart), 'ms');
              contentParts.push({
                type: 'image_url',
                image_url: {
                  url: inlined.url,
                  detail: inlined.detail || 'auto'
                }
              });
            }
          }

          messages.push({
            role: msg.role,
            content: contentParts
          } as OpenAI.Chat.ChatCompletionMessageParam);
        }
      }
    }
  logger.debug('[OpenAIService] Built messages in', Math.round(performance.now() - tContextStart), 'ms');

    // Add current user prompt — with images as multimodal content when provided
    if (imageUrls && imageUrls.length > 0) {
      const contentParts: Array<OpenAI.Chat.ChatCompletionContentPart> = [];
      
      // Add each image (limit to 4 to avoid token overflow)
      const imagesToSend = imageUrls.slice(0, 4);
      for (const imgUrl of imagesToSend) {
        try {
          const base64Url = await this.toBase64DataUrl(imgUrl);
          if (base64Url) {
            contentParts.push({
              type: 'image_url',
              image_url: {
                url: base64Url,
                detail: 'low'  // Use low detail to reduce token cost for prompt improvement
              }
            });
          }
        } catch (e) {
          logger.warn('[OpenAIService] Failed to convert image for multimodal prompt:', e);
        }
      }
      
      // Add the text prompt
      contentParts.push({ type: 'text', text: prompt });
      
      messages.push({
        role: 'user',
        content: contentParts
      } as OpenAI.Chat.ChatCompletionMessageParam);
      
      logger.debug(`[OpenAIService] Added ${contentParts.length - 1} image(s) to multimodal user message`);
    } else {
      // Simple text-only prompt
      messages.push({
        role: 'user',
        content: prompt
      });
    }

    // Console debug output for prompt being sent
    console.log('🤖 [OpenAI Request]', {
      model: MODELS.OPENAI,
      prompt,
      systemPrompt,
      contextMessages: context?.messages?.length || 0,
      totalMessages: messages.length
    });

    // All requests go through server proxy for credit tracking
    try {
      const tApiStart = performance.now();
      
      // Get current user for credit tracking
      const { data: { user } } = await supabase.auth.getUser();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (user?.id) {
        headers['x-user-id'] = user.id;
      }
      
      const res = await fetch(`${this.apiBase}/api/openai/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          prompt,
          systemPrompt: systemPrompt || undefined,
          model: MODELS.OPENAI,
          context: context || undefined
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Server responded ${res.status}`);
      }
      const data = await res.json();
      logger.debug('[OpenAIService] API latency (server)', Math.round(performance.now() - tApiStart), 'ms');

      const responseContent: string = (data.content || '').toString();

      const contextMessages: ChatMessage[] = context?.messages || [];
      const updatedMessages: ChatMessage[] = [
        ...contextMessages.slice(-LIMITS.MAX_CONTEXT_MESSAGES),
        { role: 'user' as const, content: prompt },
        { role: 'assistant' as const, content: responseContent }
      ].slice(-LIMITS.MAX_CONTEXT_MESSAGES);

      const tEnd = performance.now();
      logger.debug('[OpenAIService] Total generateResponse time', Math.round(tEnd - tStart), 'ms');
      return {
        content: responseContent,
        context: { messages: updatedMessages }
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('OpenAI API Error:', error);
      if (errorMessage.includes('billing')) {
        throw new Error(API_ERRORS.BILLING_REQUIRED);
      }
      if (errorMessage.includes('rate limit')) {
        throw new Error(API_ERRORS.RATE_LIMIT);
      }
      throw new Error(`Failed to generate response: ${errorMessage}`);
    }
  }

  /**
   * Analyze image using OpenAI Vision API
   */
  async analyzeImage(
    imageFile: File,
    prompt: string = "Describe this image in detail.",
    _options: VisionOptions = {}
  ): Promise<string> {
    try {
      // Convert image to base64
      const base64Image = await this.fileToBase64(imageFile);
      
      // All requests go through server proxy for credit tracking
      const { data: { user } } = await supabase.auth.getUser();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (user?.id) {
        headers['x-user-id'] = user.id;
      }
      
      const res = await fetch(`${this.apiBase}/api/openai/vision`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          prompt,
          imageUrl: `data:image/jpeg;base64,${base64Image}`,
          model: MODELS.OPENAI_VISION
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        // The server sends `error` as a short code ("Insufficient credits") and
        // `message` as the detail the user actually needs ("Required: 12, Available:
        // 3"). Reading only `error` threw the numbers away, so a 402 told the user
        // nothing about how short they were.
        const parts = [err.error, err.message]
          .filter((part: unknown): part is string => typeof part === 'string' && part.trim().length > 0)
          .map((part: string) => part.trim());
        // Do not repeat the code when the server already embedded it in the message.
        const detail = parts.length === 2 && parts[1].includes(parts[0]) ? parts[1] : parts.join(' — ');
        throw new Error(detail || `Server responded ${res.status}`);
      }
      const data = await res.json();
      return (data.content || '').toString().trim();

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('OpenAI Vision API Error:', error);
      throw new Error(`Failed to analyze image: ${errorMessage}`);
    }
  }

  /**
   * Stream response via server SSE endpoint.
   * onToken is called for each delta; final content is returned when stream completes.
   */
  async generateResponseStream(
    prompt: string,
    systemPrompt: string | null = null,
    context: ConversationContext | null = null,
    onToken?: (delta: string) => void,
    model: string = MODELS.OPENAI
  ): Promise<OpenAIResponse> {
    // Build messages same as non-streaming path to preserve multimodal context
    const messages: Array<OpenAI.Chat.ChatCompletionMessageParam> = [];
    if (systemPrompt) {
      messages.push({ role: 'system', content: systemPrompt });
    }
    const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
      let binary = '';
      const bytes = new Uint8Array(buffer);
      const len = bytes.byteLength;
      for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i]);
      return btoa(binary);
    };
    const inlineImageUrl = async (url: string): Promise<{ url: string; detail?: 'auto' | 'low' | 'high' }> => {
      try {
        if (!url) return { url };
        let actualUrl = url.startsWith('__BLOB_URL__:') ? url.replace('__BLOB_URL__:', '') : url;
        if (actualUrl.startsWith('data:')) return { url: actualUrl, detail: 'auto' };
        
        // Handle relative URLs (e.g., /api/media/proxy?key=...) by making them absolute
        // OpenAI cannot access relative URLs, so we must fetch and inline them
        if (actualUrl.startsWith('/')) {
          actualUrl = `${window.location.origin}${actualUrl}`;
          logger.debug('[OpenAIService] Converted relative URL to absolute:', actualUrl);
        }
        
        // For blob URLs, http/https URLs (including localhost proxy URLs), fetch and inline as base64
        // OpenAI cannot access localhost URLs, so we must inline them
        if (actualUrl.startsWith('blob:') || actualUrl.startsWith('http://') || actualUrl.startsWith('https://')) {
          const resp = await fetch(actualUrl);
          if (!resp.ok) {
            console.warn(`[OpenAIService] Failed to fetch image for inlining: ${actualUrl}`);
            return { url: actualUrl, detail: 'auto' };
          }
          const contentType = resp.headers.get('content-type') || 'image/png';
          const buf = await resp.arrayBuffer();
          const b64 = arrayBufferToBase64(buf);
          return { url: `data:${contentType};base64,${b64}`, detail: 'auto' };
        }
        return { url: actualUrl, detail: 'auto' };
      } catch (err) {
        console.warn(`[OpenAIService] Error inlining image URL: ${url}`, err);
        return { url, detail: 'auto' };
      }
    };
    if (context && context.messages) {
      const recent = context.messages.slice(-LIMITS.MAX_CONTEXT_MESSAGES);
      for (const msg of recent) {
        if (typeof msg.content === 'string') {
          messages.push({ role: msg.role, content: msg.content } as any);
        } else if (Array.isArray(msg.content)) {
          const parts: Array<OpenAI.Chat.ChatCompletionContentPart> = [];
          for (const part of msg.content) {
            if (part.type === 'text') {
              parts.push({ type: 'text', text: part.text });
            } else if (part.type === 'image') {
              const inlined = await inlineImageUrl(part.imageUrl);
              parts.push({ type: 'image_url', image_url: { url: inlined.url, detail: inlined.detail || 'auto' } });
            }
          }
          messages.push({ role: msg.role, content: parts } as any);
        }
      }
    }
    messages.push({ role: 'user', content: prompt });

    // Get current user for credit tracking
    const { data: { user } } = await supabase.auth.getUser();
    const streamHeaders: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'text/event-stream' };
    if (user?.id) {
      streamHeaders['x-user-id'] = user.id;
    }

    const apiUrl = `${this.apiBase}/api/openai/chat/stream`;
    const res = await fetch(apiUrl, {
      method: 'POST',
      headers: streamHeaders,
      body: JSON.stringify({ prompt, systemPrompt: systemPrompt || undefined, model, context: { messages } })
    });
    if (!res.ok || !res.body) {
      const err = await res.text().catch(() => '');
      throw new Error(err || `Streaming failed with status ${res.status}`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let full = '';
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 2);
        if (!raw) continue;
        if (raw.startsWith(':')) continue; // heartbeat
        // Parse SSE lines which may be multi-line ("event:" then "data:")
        const lines = raw.split('\n');
        let event: string | null = null;
        let dataStr = '';
        for (const line of lines) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          if (line.startsWith('data:')) dataStr += line.slice(5).trim();
        }
        if (!dataStr) continue;
        try {
          const payload = JSON.parse(dataStr);
          if (event === 'done' && typeof payload.content === 'string') {
            full = payload.content;
          } else if (event === 'error') {
            // Handle error event from server
            throw new Error(payload.message || 'Streaming failed');
          } else if (typeof payload.delta === 'string') {
            full += payload.delta;
            onToken?.(payload.delta);
          }
        } catch (err) {
          // If it's an Error we threw, re-throw it
          if (err instanceof Error) {
            throw err;
          }
          // Otherwise ignore malformed chunks
        }
      }
    }

    return {
      content: full,
      context: { messages: [ { role: 'user' as const, content: prompt }, { role: 'assistant' as const, content: full } ] }
    };
  }

  /**
   * Convert file to base64
   */
  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Generate image using GPT Image 1 (Responses API)
   */
  async generateImageWithGPT(
    prompt: string,
    options: {
      inputFidelity?: 'low' | 'high';
    } = {}
  ): Promise<{ url: string; revisedPrompt?: string }> {
    try {
      const { inputFidelity = 'high' } = options;

      logger.debug('[OpenAIService] Generating image with GPT Image 1:', { prompt, inputFidelity });

      // All requests go through server proxy for credit tracking
      const { data: { user } } = await supabase.auth.getUser();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (user?.id) {
        headers['x-user-id'] = user.id;
      }
      
      // Use server proxy
      const response = await fetch(`${this.apiBase}/api/openai/generate-image-gpt`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ prompt, inputFidelity })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Image generation failed');
      }

      const data = await response.json();
      return {
        url: data.url,
        revisedPrompt: data.revisedPrompt
      };
    } catch (error) {
      logger.error('[OpenAIService] GPT Image generation error:', error);
      throw error;
    }
  }

  /**
   * Generate image using DALL-E
   */
  async generateImage(
    prompt: string,
    options: {
      model?: 'gpt-image-1.5' | 'gpt-image-2';
      size?: string;
      quality?: string;
      style?: 'vivid' | 'natural';
      resolution?: string; // For credit pricing tiers
      images?: Array<{ url: string; mimeType?: string }>; // Input images for GPT Image 1.5
    } = {}
  ): Promise<{ url: string; revisedPrompt?: string }> {
    try {
      const {
        model = 'gpt-image-2',
        size = '1024x1024',
        quality = 'standard',
        style = 'vivid',
        resolution = '1024px', // Default resolution for credit pricing
        images = []
      } = options;

      logger.debug('[OpenAIService] Generating image:', { prompt, model, size, quality, style, imageCount: images.length });

      // Convert all image URLs to base64 data URLs on the client side
      // This is necessary because the server cannot fetch relative URLs like /api/media/proxy
      const convertedImages: Array<{ url: string; mimeType?: string }> = [];
      if (images.length > 0) {
        logger.debug('[OpenAIService] Converting', images.length, 'image(s) to base64...');
        for (const img of images) {
          const base64Url = await this.toBase64DataUrl(img.url);
          if (base64Url) {
            convertedImages.push({ url: base64Url, mimeType: img.mimeType });
            logger.debug('[OpenAIService] Successfully converted image to base64');
          } else {
            logger.warn('[OpenAIService] Failed to convert image, skipping:', img.url.substring(0, 50));
          }
        }
        logger.debug('[OpenAIService] Converted', convertedImages.length, 'of', images.length, 'images');
      }

      // All requests go through server proxy for credit tracking
      const { data: { user } } = await supabase.auth.getUser();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (user?.id) {
        headers['x-user-id'] = user.id;
      }
      
      // Use server proxy
      const response = await fetch(`${this.apiBase}/api/openai/generate-image`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ prompt, model, size, quality, style, resolution, images: convertedImages })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Image generation failed');
      }

      const data = await response.json();
      return {
        url: data.url,
        revisedPrompt: data.revisedPrompt
      };
    } catch (error) {
      logger.error('[OpenAIService] Image generation error:', error);
      throw error;
    }
  }

  isConfigured(): boolean {
    // Service is always configured in browser - we use server proxy (same-origin or explicit base)
    // In production, apiBase is '' (same-origin) which is valid, not unconfigured
    return typeof window !== 'undefined';
  }
}

// Create singleton instance
const openAIService = new OpenAIService();
export default openAIService;
